import React, { useState, useEffect, useRef } from "react";
import { Input } from "./input";
import { api } from "../../utils/api";

export function AirportAutocomplete({ placeholder, value, onChange, className }) {
    const [query, setQuery] = useState(value || "");
    const [results, setResults] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);
    const debounceTimeout = useRef(null);

    useEffect(() => {
        setQuery(value || "");
    }, [value]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSearch = (searchTerm) => {
        setQuery(searchTerm);
        onChange(searchTerm, null); // The fallback mechanism - user inputs free text
        
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

        if (!searchTerm.trim()) {
            setResults([]);
            setIsOpen(false);
            return;
        }

        debounceTimeout.current = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await api.getAirports(searchTerm);
                const data = res.data;
                if (data && data.length > 0) {
                    setResults(data);
                    setIsOpen(true);
                } else {
                    setResults([]);
                    setIsOpen(false);
                }
            } catch (err) {
                console.error("Error fetching airports:", err);
            } finally {
                setLoading(false);
            }
        }, 300);
    };

    const handleSelect = (airport) => {
        const text = `${airport.iata_code} - ${airport.name}`;
        setQuery(text);
        onChange(text, airport);
        setIsOpen(false);
    };

    return (
        <div ref={wrapperRef} className={`relative ${className || ""}`}>
            <Input
                placeholder={placeholder}
                value={query}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => { if (results.length > 0) setIsOpen(true); }}
            />
            {isOpen && results.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {loading && <div className="p-2 text-sm text-gray-500">Loading...</div>}
                    {!loading && results.map((airport) => (
                        <div
                            key={airport.id}
                            className="p-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => handleSelect(airport)}
                        >
                            <span className="font-semibold">{airport.iata_code}</span> - {airport.name}
                            <div className="text-xs text-gray-500">{airport.municipality}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
